import type { MouseEventHandler, ReactNode } from 'react';
import { forwardRef } from 'react';
import type { FlexProps, LayoutProps, TableCellProps } from '@chakra-ui/react';
import { Td, Text } from '@chakra-ui/react';
import type { Cell } from '@tanstack/react-table';

import { ApiDocumentSource } from '@izimi/api';
import type { DocumentEntity } from '@izimi/modules';
import { getExtension } from '@izimi/utils';

import { DateCell } from './cells/DateCell';
import { NameCell } from './cells/NameCell';

export interface BaseCellProps {
  document: DocumentEntity;
  cell: Cell<DocumentEntity, unknown>;
  onClick?: MouseEventHandler;
  onDoubleClick?: MouseEventHandler;
}

interface NameCellProps extends BaseCellProps {
  fileIconColor: string;
  searchValue?: string;
  children?: ReactNode;
  showUnseen?: boolean;
  tableCellProps?: TableCellProps;
  tableCellContentProps?: FlexProps;
  isSeen?: boolean;
  isImportant?: boolean;
}

const TABLE_CELL_CONTENT_PROPS = {
  pl: {
    base: 3,
    md: 0,
  },
};

export const DocumentNameCell = forwardRef<
  HTMLTableCellElement,
  Omit<NameCellProps, 'cell'> & Partial<NameCellProps>
>(({ document, tableCellContentProps, ...props }, ref) => {
  const isCertified = document.isCertified || document.isNabanDataType;

  return (
    <NameCell
      ref={ref}
      name={document.name}
      textToHightlight={document.fileNameWithoutExtension}
      isCertified={isCertified}
      fileExtension={getExtension(document.name)}
      withFodFinIcon={document.source === ApiDocumentSource.Tuyeau}
      withNotaryIcon={document.isUploadedByNotary}
      withCheckmark={document.isCopiedFromInheritedVault}
      isPending={document.isPending}
      isSeen={document.isSeen}
      tableCellContentProps={tableCellContentProps ?? TABLE_CELL_CONTENT_PROPS}
      {...props}
    />
  );
});

type EditedDateCellProps = {
  dateLocale: Locale | undefined;
} & BaseCellProps &
  LayoutProps;

export const EditedDateCell = forwardRef<HTMLTableCellElement, EditedDateCellProps>(
  ({ cell, document, ...props }, ref) => (
    <DateCell id={cell.id} date={document.modificationDate} ref={ref} {...props} />
  ),
);

interface OwnerCellProps extends BaseCellProps {
  documentOwnerText: string;
}

export const OwnerCell = forwardRef<HTMLTableCellElement, OwnerCellProps>(
  ({ cell, onClick, documentOwnerText }, ref) => {
    return (
      <Td key={cell.id} ref={ref} onClick={onClick} display={{ base: 'none', xl: 'table-cell' }}>
        <Text variant='tableCell'>{documentOwnerText}</Text>
      </Td>
    );
  },
);
